import { SVGIcon } from '@superops/super-icons';
import { Button, Tippy } from '@superops/ui-design-system';
import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import ServiceCatalogModal from './ServiceCatatlogModal';
import { ServiceCatalogItem } from './types';
import { useMutation } from '@apollo/client';
import { AccountClient, CREATE_SERVICE_CATALOG } from '@nile/graphql';
import { AccessControl, Column, useProduct } from '@nile/components';

type ServiceCatalogOptionsType = 'PRODUCT' | 'SERVICE' | 'PACKAGE';

function CreateServiceCatalogItemBase({
  onSuccess,
  isSecondary,
  allowOnlyServiceQuantityAndProduct = false,
  isFromTicketOrProject
}: {
  onSuccess: (arg: any) => Promise<boolean>;
  isSecondary: boolean;
  allowOnlyServiceQuantityAndProduct: boolean;
  isFromTicketOrProject: boolean;
}) {
  const serviceCatalogFormRef = useRef<{
    showForm: ({
      mode,
      catalogType,
      catalogItemId
    }: {
      mode: 'NEW' | 'EDIT' | 'CLONE';
      catalogType?: ServiceCatalogOptionsType;
      catalogItemId?: string;
    }) => void;
    hideForm: () => void;
  }>(null);

  const [CreateServiceCatalogItem] = useMutation(CREATE_SERVICE_CATALOG, {
    client: AccountClient,
    onCompleted({ createServiceCatalogItem }) {
      if (!createServiceCatalogItem) return;

      const successCallback = () =>
        onSuccess?.(createServiceCatalogItem).then((isSuccess) => {
          if (isSuccess) {
            serviceCatalogFormRef.current?.hideForm();
          }
        });

      if (allowOnlyServiceQuantityAndProduct) {
        const isOffered = isFromTicketOrProject
          ? createServiceCatalogItem.offerAsWorklogItem
          : true;
        if (
          isOffered &&
          ((createServiceCatalogItem.itemType === 'SERVICE' &&
            createServiceCatalogItem.serviceTypeItem?.offeringType ===
              'QUANTITY') ||
            createServiceCatalogItem.itemType === 'PRODUCT')
        ) {
          successCallback();
        }
        return serviceCatalogFormRef.current?.hideForm();
      }

      if (!createServiceCatalogItem.offerAsWorklogItem) {
        return serviceCatalogFormRef.current?.hideForm();
      }

      successCallback();
    }
  });

  const CATALOGUE_OPTIONS: {
    key: ServiceCatalogOptionsType;
    icon: string;
    mspOnly?: boolean;
  }[] = [
    { key: 'SERVICE', icon: 'ProjectIdentifier', mspOnly: true },
    { key: 'PRODUCT', icon: 'Puzzle' }
    // { key: 'PACKAGE', icon: 'Box', mspOnly: true }
  ];

  function handleSelect(key: ServiceCatalogOptionsType) {
    serviceCatalogFormRef.current?.showForm({
      mode: 'NEW',
      catalogType: key || 'SERVICE'
    });
  }

  function saveServiceCatalogItem() {
    return ({
      formData,
      createNew
    }: {
      formData: ServiceCatalogItem;
      createNew?: boolean;
    }): Promise<unknown> => {
      return CreateServiceCatalogItem({
        variables: {
          input: formData
        }
      }).then((res) => {
        if (createNew) {
          let catalogType: ServiceCatalogOptionsType =
            res?.data?.createServiceCatalogItem?.itemType;
          if (
            ['BLOCK_HOUR', 'BLOCK_MONEY', 'SERVICE_BUNDLE'].includes(
              res?.data?.createServiceCatalogItem?.serviceTypeItem?.offeringType
            )
          ) {
            catalogType = 'PACKAGE';
          }
          setTimeout(() => {
            serviceCatalogFormRef.current?.showForm({
              mode: 'NEW',
              catalogType: catalogType || 'SERVICE'
            });
          }, 1000);
        }
      });
    };
  }

  const isMSPProduct = useProduct('MSP');

  return (
    <>
      <ServiceCatalogModal
        reference={serviceCatalogFormRef}
        saveServiceCatalogItem={saveServiceCatalogItem}
        className="w1200 vh90 bg-white rounded-2xl"
      />
      <Tippy
        inertia
        interactive
        animation="shift"
        animateFill={false}
        position="bottom-start"
        trigger="mouseenter"
        delay={150}
        theme="light"
        html={
          <Column className="br-5 border">
            {CATALOGUE_OPTIONS.filter((option) => {
              if (option?.mspOnly) {
                return !!isMSPProduct;
              }
              return true;
            }).map((option) => {
              const { key, icon } = option;
              return (
                <div
                  className="fx actr pl-20 pr-20 pb10 pt10 ptr integration-filters"
                  key={key}
                  onClick={() => handleSelect(key)}
                >
                  <span className="fx actr mr-5">
                    <SVGIcon name={icon} size="sm" />
                  </span>
                  {key}
                </div>
              );
            })}
          </Column>
        }
      >
        <Button primary={!isSecondary} icon={<SVGIcon name="Add" />}>
          <span className="fx actr g5">
            <FormattedMessage id="common.create" />
            <SVGIcon name="ArrowDown" size="sm" />
          </span>
        </Button>
      </Tippy>
    </>
  );
}

export const CreateServiceCatalogItem = function _CreateServiceCatalogItem(
  props
) {
  return (
    <AccessControl
      roleToCheck="serviceCatalog.add"
      render={<CreateServiceCatalogItemBase {...props} />}
    />
  );
};
